/**
 * MobileComunicados — Mural da escola (somente leitura). Toque no card → detalhe em bottom sheet.
 */
import { useState, useMemo, useEffect } from 'react';
import {
  Megaphone, Search, Filter, Pin, ChevronRight, Clock, Info, Building2, Landmark,
  Bell, AlertTriangle, CalendarDays, Users, type LucideIcon,
} from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useCrud } from '../../api/hooks/useCrud';
import { MobilePageWrapper } from '../../components/layout/MobilePageWrapper';
import {
  ComunicadoLeituraSheet,
  type ComunicadoLeitura,
} from '../../components/comunicados/ComunicadoLeituraSheet';

interface Comunicado extends ComunicadoLeitura {
  turmaId?: string;
  ativo: boolean;
}

const TIPOS = ['AVISO', 'URGENTE', 'EVENTO', 'REUNIAO', 'LEMBRETE'] as const;

const TIPO_ICON: Record<(typeof TIPOS)[number], { Icon: LucideIcon; color: string; title: string }> = {
  AVISO: { Icon: Bell, color: '#d97706', title: 'Avisos' },
  URGENTE: { Icon: AlertTriangle, color: '#dc2626', title: 'Urgentes' },
  EVENTO: { Icon: CalendarDays, color: '#7c3aed', title: 'Eventos' },
  REUNIAO: { Icon: Users, color: '#166534', title: 'Reuniões' },
  LEMBRETE: { Icon: Clock, color: '#2563eb', title: 'Lembretes' },
};
const DESTINATARIO_LABEL: Record<string, string> = {
  TODOS: 'Todos',
  TURMA: 'Turma',
  PROFESSORES: 'Professores',
  RESPONSAVEIS: 'Responsáveis',
};
const TIPO_COLORS: Record<string, { bg: string; color: string; border: string }> = {
  AVISO: { bg: '#fef3c7', color: '#d97706', border: '#fde68a' },
  URGENTE: { bg: '#fee2e2', color: '#dc2626', border: '#fecaca' },
  EVENTO: { bg: '#f3e8ff', color: '#7c3aed', border: '#e9d5ff' },
  REUNIAO: { bg: '#dcfce7', color: '#166534', border: '#bbf7d0' },
  LEMBRETE: { bg: '#dbeafe', color: '#2563eb', border: '#bfdbfe' },
};

export function MobileComunicados() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useCrud<Comunicado>('comunicados');
  const [busca, setBusca] = useState('');
  const [filtrosAbertos, setFiltrosAbertos] = useState(false);
  const [filtroTipo, setFiltroTipo] = useState('');
  const [detalhe, setDetalhe] = useState<Comunicado | null>(null);

  useEffect(() => {
    const refresh = () => {
      queryClient.invalidateQueries({ queryKey: ['comunicados'] });
    };
    window.addEventListener('ac-push-received', refresh);
    const poll = window.setInterval(refresh, 15000);
    return () => {
      window.removeEventListener('ac-push-received', refresh);
      window.clearInterval(poll);
    };
  }, [queryClient]);

  const tipoStats = useMemo(
    () =>
      TIPOS.map((t) => ({
        tipo: t,
        count: data.filter((c) => c.tipo === t && c.ativo).length,
        ...TIPO_ICON[t],
      })),
    [data],
  );

  const filteredRows = useMemo(() => {
    let rows = data;
    if (filtroTipo) rows = rows.filter((c) => c.tipo === filtroTipo);
    const q = busca.trim().toLowerCase();
    if (q) {
      rows = rows.filter(
        (c) =>
          c.titulo.toLowerCase().includes(q) ||
          c.corpo?.toLowerCase().includes(q) ||
          c.tipo.toLowerCase().includes(q),
      );
    }
    return rows;
  }, [data, busca, filtroTipo]);

  const fixados = data.filter((c) => c.fixado && c.ativo);

  const formatDate = (d: string) => {
    try {
      return new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
    } catch {
      return d;
    }
  };

  const origemLabel = (c: Comunicado) =>
    c.municipio?.nome
      ? `Prefeitura · ${c.municipio.nome}`
      : c.escola?.nome || '';

  return (
    <MobilePageWrapper title="Comunicados" icon={Megaphone} iconColor="#fbbf24">
      <div style={{ marginTop: -4 }}>
        <div
          style={{
            display: 'flex',
            gap: 8,
            alignItems: 'flex-start',
            padding: '8px 10px',
            marginBottom: 8,
            borderRadius: 10,
            background: '#eff6ff',
            border: '1px solid #bfdbfe',
            fontSize: '0.68rem',
            color: '#1e40af',
            lineHeight: 1.4,
          }}
        >
          <Info size={14} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>
            Toque em um comunicado para ver a mensagem completa. Para falar com a família de um
            aluno, use <strong>Meus Alunos → Avisar responsável</strong>.
          </span>
        </div>

        <div className="comunicados-kpi-row">
          {tipoStats.map((t) => {
            const { Icon, color, title } = t;
            return (
              <div
                key={t.tipo}
                className={`comunicados-kpi-item ${filtroTipo === t.tipo ? 'active' : ''}`}
                onClick={() => setFiltroTipo(filtroTipo === t.tipo ? '' : t.tipo)}
                title={title}
                role="button"
                aria-label={`${title}: ${t.count}`}
                aria-pressed={filtroTipo === t.tipo}
              >
                <span className="comunicados-kpi-icon">
                  <Icon size={18} strokeWidth={2.2} color={color} aria-hidden />
                </span>
                <span className="comunicados-kpi-value">{t.count}</span>
              </div>
            );
          })}
        </div>

        <div className="comunicados-filter-bar">
          <div className="comunicados-search-wrap">
            <Search size={15} className="comunicados-search-icon" aria-hidden />
            <input
              type="search"
              placeholder="Buscar comunicado..."
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              className="comunicados-search-input"
              autoComplete="off"
            />
          </div>
          <button
            type="button"
            className="comunicados-filter-btn"
            onClick={() => setFiltrosAbertos(!filtrosAbertos)}
            aria-label="Filtros"
          >
            <Filter size={15} />
          </button>
        </div>

        {filtrosAbertos && (
          <div className="diario-filter-panel">
            <select value={filtroTipo} onChange={(e) => setFiltroTipo(e.target.value)}>
              <option value="">Todos os tipos</option>
              {TIPOS.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
        )}

        {fixados.length > 0 && (
          <div className="comunicados-mural">
            <div className="comunicados-mural-header">
              <Pin size={12} style={{ color: '#92400e' }} />
              <span>Mural fixado</span>
            </div>
            {fixados.map((c) => {
              const tc = TIPO_COLORS[c.tipo] || {
                bg: '#f3f4f6',
                color: '#6b7280',
                border: '#e5e7eb',
              };
              return (
                <div
                  key={c.id}
                  className="comunicados-mural-item"
                  style={{ cursor: 'pointer' }}
                  onClick={() => setDetalhe(c)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && setDetalhe(c)}
                >
                  <span
                    style={{
                      fontWeight: 600,
                      fontSize: '0.72rem',
                      flex: 1,
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {c.titulo}
                  </span>
                  <span
                    style={{
                      fontSize: '0.5rem',
                      fontWeight: 700,
                      padding: '1px 5px',
                      borderRadius: 4,
                      background: tc.bg,
                      color: tc.color,
                    }}
                  >
                    {c.tipo}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {isLoading ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>
            Carregando...
          </div>
        ) : filteredRows.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 30, color: '#9ca3af' }}>
            <Megaphone size={28} style={{ margin: '0 auto 8px', color: '#d1d5db' }} />
            <p style={{ fontWeight: 600, fontSize: '0.78rem' }}>Nenhum comunicado</p>
          </div>
        ) : (
          <div className="comunicados-list">
            {filteredRows.map((c) => {
              const tc = TIPO_COLORS[c.tipo] || {
                bg: '#f3f4f6',
                color: '#6b7280',
                border: '#e5e7eb',
              };
              const origem = origemLabel(c);
              return (
                <div
                  key={c.id}
                  className={`comunicados-card ${c.tipo === 'URGENTE' ? 'comunicados-card--urgente' : ''}`}
                  style={{ borderLeftColor: tc.color }}
                  onClick={() => setDetalhe(c)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && setDetalhe(c)}
                >
                  <div className="comunicados-card-header">
                    <div className="comunicados-card-info">
                      <div className="comunicados-card-title">
                        {c.fixado && (
                          <Pin size={10} style={{ color: '#d97706', flexShrink: 0 }} />
                        )}
                        <span>{c.titulo}</span>
                      </div>
                      <div className="comunicados-card-meta">
                        <span
                          className="comunicados-card-tag"
                          style={{ background: tc.bg, color: tc.color }}
                        >
                          {c.tipo}
                        </span>
                        <span className="comunicados-card-tag dest">
                          {DESTINATARIO_LABEL[c.destinatario] || c.destinatario}
                        </span>
                        <span className="comunicados-card-date">
                          <Clock size={9} /> {formatDate(c.criadoEm)}
                        </span>
                      </div>
                      {c.corpo && <p className="comunicados-card-preview">{c.corpo}</p>}
                      {origem && (
                        <div className="comunicados-card-origem">
                          {c.municipio?.nome ? (
                            <Landmark size={10} style={{ display: 'inline', marginRight: 4, verticalAlign: -1 }} />
                          ) : (
                            <Building2 size={10} style={{ display: 'inline', marginRight: 4, verticalAlign: -1 }} />
                          )}
                          {origem}
                        </div>
                      )}
                    </div>
                    <div className="comunicados-card-chevron">
                      <ChevronRight size={16} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <ComunicadoLeituraSheet comunicado={detalhe} onClose={() => setDetalhe(null)} />
    </MobilePageWrapper>
  );
}
